import { SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import CheckButton from 'components/CheckButton'
import LinearLoader from 'components/LinearLoader'
import ScoreBlock from 'components/serviceInfo/ScoreBlock'
import ServiceStatsContainer from 'containers/ServiceStatsContainer'
import { useEffect } from 'react'

interface MatchesPerRequest {
    avgMatchPerRequest: number
    nrMatches: number
}

type CheckPointPercentages = Record<string, number | string>

interface MatchStatsBase {
    matchesPerRequest: MatchesPerRequest
    checkPointPercentages: CheckPointPercentages
}

interface MatchStats extends MatchStatsBase {
    history: MatchStatsBase[]
}

interface StatsOptions {
    days?: number
    subscribed?: string
    isFetching?: boolean
}

interface ServiceStatsContainerProps {
    serviceId: number
    changeOptions: (serviceId: number, type: string, options: Record<string, unknown>) => void
    fetchStats: (serviceId: number, type: string) => void
    fetchService: (serviceId: number) => void
    getStatsAndOptions: (type: string) => {
        stats: MatchStats | null
        options: StatsOptions
    }
}

const ServiceMatchStats = ({
    serviceId,
    changeOptions,
    fetchStats,
    fetchService,
    getStatsAndOptions,
}: ServiceStatsContainerProps) => {
    // Fetch stats when component mounts or when serviceId changes
    useEffect(() => {
        changeOptions(serviceId, 'matchStats', { historyFactor: serviceId ? 2 : null })
        fetchStats(serviceId, 'matchStats')
        if (serviceId) {
            fetchService(serviceId)
        }
    }, [serviceId])

    const _changeOptions = (options: Record<string, unknown>) => {
        changeOptions(serviceId, 'matchStats', { ...options, didInvalidate: true })
        fetchStats(serviceId, 'matchStats')
    }

    const onChangeDays = (e: SelectSimpleChangeEvent) => {
        _changeOptions({ days: e.target.value })
    }

    const { stats, options } = getStatsAndOptions('matchStats')
    if (!stats) {
        return null
    }
    const { matchesPerRequest, checkPointPercentages, history } = stats

    const checkPointStatsRows: Array<[string, number, ...number[]]> = Object.keys(
        checkPointPercentages,
    ).map((key) => {
        const currentValue = Math.round(parseFloat(String(checkPointPercentages[key] || 0)))
        const historyValues = history.map(({ checkPointPercentages }: MatchStatsBase) =>
            Math.round(parseFloat(String(checkPointPercentages[key] || 0))),
        )
        return [key, currentValue, ...historyValues] as [string, number, ...number[]]
    })

    return (
        <div className="bg-white border border-grey-500 rounded-xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.05)] mb-6">
            {options.isFetching && <LinearLoader />}
            <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
                <div>
                    <h3 className="text-xl font-semibold text-bodyDark m-0 mb-1.5">Matches</h3>
                    <p className="text-[13px] text-grey-800 m-0 leading-[1.4] max-w-[500px]">
                        Overview of match statistics showing how many providers are matched to
                        customer requests and their progression through different stages
                    </p>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    <SelectSimple
                        value={options.days || 30}
                        onChange={onChangeDays}
                        className="select-stealth select-min select-wide"
                        options={[
                            { value: 1, name: '1 day' },
                            { value: 7, name: '7 days' },
                            { value: 10, name: '10 days' },
                            { value: 30, name: '30 days' },
                            { value: 90, name: '3 months' },
                            { value: 180, name: '6 months' },
                            { value: 365, name: '1 year' },
                        ]}
                    />
                    <CheckButton
                        checked={options.subscribed}
                        msg="Premium"
                        onChange={() =>
                            _changeOptions({ subscribed: options.subscribed ? '' : '1' })
                        }
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3 mb-4">
                <ScoreBlock
                    value={matchesPerRequest.avgMatchPerRequest}
                    prevValue={history[0] ? history[0].matchesPerRequest.avgMatchPerRequest : null}
                    name="Average matches p/request"
                />
                <ScoreBlock
                    value={matchesPerRequest.nrMatches}
                    prevValue={history[0] ? history[0].matchesPerRequest.nrMatches : null}
                    name="Total nr of matches"
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mt-4">
                {checkPointStatsRows.map(([name, val]) => {
                    return (
                        <ScoreBlock
                            key={name}
                            loading={options.isFetching}
                            name={name}
                            prevValue={
                                history[0]
                                    ? (history[0].checkPointPercentages[name] as
                                          | number
                                          | string
                                          | null)
                                    : null
                            }
                            value={val}
                            isPercentage
                        />
                    )
                })}
            </div>
        </div>
    )
}

export default ServiceStatsContainer(ServiceMatchStats)
